import { useEffect, useState, useRef } from 'react';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'library/components/item/helpers';
import ItemFields from 'library/components/item/fields';
import PickRadioModal from 'library/components/modal/pick-radio';
import PickAndCreateSet from 'library/components/modal/create-and-pick-set';
import SearchByColorModal from 'library/components/modal/pick-color';
// eslint-disable-next-line import/no-duplicates
import AddWishlistModal from 'library/components/modal/batch-confirm';
// eslint-disable-next-line import/no-duplicates
import DeleteItemsModal from 'library/components/modal/batch-confirm';
import { getItem as getItemSelector } from 'store/items/selectors';
import {
  createImageByUrlData, createImageByUrlLoading,
  createImageData,
  createImageLoading,
} from 'store/images/selectors';
import {
  createItem,
  deleteItems, resetNextItemData, setNextItemData,

} from 'store/items/actions';
import {
  createImage, createImageByUrl, resetImage, resetImageByUrl,
} from 'store/images/actions';
import { showSuccessNotification } from 'store/shared/actions';
import useCategoryList from 'library/hooks/use-deferred-data/use-category-list';
import useSetList from 'library/hooks/use-deferred-data/use-set-list';
import Header from '../header';
import { TransitionDuration } from '../../../constants/styles-config';
import UploadModal from '../image-upload';
import SliderField from '../../form/image-slider';
import { Content, Container, SliderContainer } from './styles';
import {
  StyledForm,
  FooterContainer, CancelButton, ConfirmButton,
} from '../styles';
import ITEM_VALIDATION from './helpers';
import { TABS } from '../../form/color-picker/component';

const getDefaultValues = (defaultFormData = {}) => ({
  name: '',
  tags: [],
  category: null,
  images: [],
  colors: [],
  storage: null,
  barcode: '',
  price: '',
  note: '',
  sets: [],
  manufacturer: null,
  count: 1,
  ...defaultFormData,
});

const ManuallyModal = ({
  isOpened,
  onClose,
  defaultData = {},
  disabledFields = [],
  isAddMoreEnabled = false,
  labelMaps,
  onCreateMore,
  cancelText = 'Cancel',
  saveText = 'Save item',
  saveAndCreateAnotherButtonText = 'Save this one and create another one',
  onCancel,
}) => {
  const dispatch = useDispatch();
  const [isUploadOpened, setUploadOpened] = useState(false);
  const [isPickCategoryModalOpened, togglePickCategoryModal] = useState(false);
  const [isSearchByColorModalOpened, toggleSearchByColorModal] = useState(false);
  const [isAddToSetModalOpened, toggleAddToSetModal] = useState(false);
  const data = useSelector(getItemSelector);
  const isImageLoading = useSelector(createImageLoading);
  const newImageData = useSelector(createImageData);
  const {
    is_barcode = false, category_name, manufacturer_name, storage_name, ...defaultDataFormatted
  } = defaultData;
  const newImageByUrlData = useSelector(createImageByUrlData);
  const isImageByUrlLoading = useSelector(createImageByUrlLoading);
  const [isAddItemsToWishlistModalOpened, toggleAddItemsToWishlistModal] = useState(false);
  const [isDeleteItemsModalOpened, toggleDeleteItemsModal] = useState(false);
  const defaultDataMarkerRef = useRef(false);
  const { options: categories, onQueryChange: onCategoryQueryChange, labelMap: categoryMap } = useCategoryList(
    {
      skip: false,
      defaultSnapshot: labelMaps?.categories ? {
        ...labelMaps.categories,
      } : undefined,
      resetOnUnmount: false,
    },
  );

  const {
    options: setOptions,
    isLoading: isSetListLoading,
    labelMap: setMap,
    onQueryChange: onSetQueryChange,
  } = useSetList(
    !isAddToSetModalOpened,
  );

  const methods = useForm({
    resolver: yupResolver(ITEM_VALIDATION),
    mode: 'onChange',
    defaultValues: getDefaultValues(),
    shouldUnregister: true,
  });
  const {
    isValid,
  } = methods.formState;

  const handleSubmit = (values, isNewItemPlanned = false) => {
    const formValues = methods.getValues();

    const dirtyFieldsExtended = {
      ...Object.keys(formValues).reduce((result, key) => ({
        ...result,
        ...(
          (
            Array.isArray(formValues[key]) && formValues[key].length) || (!!formValues[key] !== false) ? { [key]: true } : {}
        ),
      }), {}),
    };

    // eslint-disable-next-line no-shadow
    const data = format({
      values: formValues,
      dirtyFields: dirtyFieldsExtended,
      defaultValues: getDefaultValues(),
      isCreate: true,
    });
    if(!isNewItemPlanned) {
      dispatch(resetNextItemData());
    }

    dispatch(createItem({
      data: {
        ...JSON.parse(JSON.stringify(data)),
        ...(is_barcode ? { is_barcode } : {}),
      },
    }));

    if(isNewItemPlanned) {
      if (onCreateMore) {
        onCreateMore();
        onClose();
      } else {
        dispatch(setNextItemData({
          name: values.name,
          category: values.category,
          sets: values.sets,
          storage: values.storage,
          manufacturer: values.manufacturer,
        }));
        onClose(true);
      }
    } else {
      onClose();
    }
  };

  const handleScroll = () => {};

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll);

    return () => {
      setTimeout(() => {
        window.removeEventListener('scroll', handleScroll);
      }, 300);
    };
  }, [defaultDataFormatted]);

  useEffect(() => {
    if (isOpened) {
      setTimeout(() => {
        methods.setFocus('name');
      }, 100);
    } else {
      defaultDataMarkerRef.current = false;
    }
  }, [isOpened]);

  useEffect(() => {
    if (Object.keys(defaultData).length > 0 && !defaultDataMarkerRef.current && isOpened) {
      defaultDataMarkerRef.current = true;
      const newDefaultValues = getDefaultValues(defaultDataFormatted);

      methods.reset(newDefaultValues, {
        keepDefaultValues: false,
        keepValues: true,
        keepDirtyValues: true,
        keepDirty: true,
        keepTouched: true,
      });

      if(newDefaultValues.manufacturer?.length > 0) {
        methods.setValue('manufacturer', newDefaultValues.manufacturer, {
          shouldDirty: true,
        });
      }

      if(newDefaultValues.storage?.length > 0) {
        methods.setValue('storage', newDefaultValues.storage, {
          shouldDirty: true,
        });
      }
    }
  }, [Object.keys(defaultData).length > 0, defaultDataFormatted, isOpened]);

  const handleUploadModalOpen = () => {
    setUploadOpened(true);
  };
  const handleUploadModalClose = () => {
    setUploadOpened(false);
  };

  const handlePickCategoryModalOpen = () => {
    togglePickCategoryModal(true);
  };
  const handlePickCategoryModalClose = () => {
    togglePickCategoryModal(false);
  };

  const handleAddToSetModalOpen = () => {
    toggleAddToSetModal(true);
  };

  const handleAddToSetModalClose = () => {
    toggleAddToSetModal(false);
  };

  const handleAddItemsToWishlistModalOpen = () => {
    toggleAddItemsToWishlistModal(true);
  };
  const handleAddItemsToWishlistModalClose = () => {
    toggleAddItemsToWishlistModal(false);
  };

  const handleDeleteItemsModalOpen = () => {
    toggleDeleteItemsModal(true);
  };
  const handleDeleteItemsModalClose = () => {
    toggleDeleteItemsModal(false);
  };

  const fileValidation = yup.object().shape({
    files: yup.array().of(yup.object().shape({
      id: yup.number(),
    })).min(1),
    images: yup.mixed(),
    urls: yup.array().of(yup.object().shape({
      value: yup.string().url().test(
        'validFormat',
        'Invalid image extension, must be one of: jpg, webp, gif, png.',
        (value) => {
          if (typeof value !== 'string') {
            return false;
          }

          if (value === '') {
            return true;
          }

          return (value.match(/^http[^?]*.(jpg|png|gif|webp)(\?(.*))?$/gmi) !== null);
        },
      ),
    })).min(1),
  });
  const categoryValidation = yup.object().shape({
    category: yup.number().required(),
  });

  const setValidation = yup.object().shape({
    // eslint-disable-next-line react/forbid-prop-types
    sets: yup.array().of(yup.number().required()).default([]),
  });

  const onFileRest = () => {
    dispatch(resetImage());
  };

  const onFileUpload = (file) => {
    dispatch(createImage({ data: file }));
  };

  const onFileSubmit = ({ files = [] }) => {
    const currentValue = methods.getValues('images');

    const newValue = [
      ...currentValue,
      ...files.map((file) => ({ ...file, isNew: true })),
    ];

    if(currentValue.length) {
      methods.reset({
        ...methods.getValues(),
        images: newValue,
      });
    }
    //
    // const newValue = [
    //   ...currentValue,
    //   ...files.map((file) => ({ ...file, isNew: true })),
    // ];

    methods.setValue('images', newValue, { shouldDirty: true, shouldTouch: true });

    // methods.reset({
    //   ...methods.getValues(),
    //   images: newValue,
    // }, { keepErrors: true, keepDirty: true });

    // methods.resetField('images', {
    //   defaultValue: [...methods.getValues('images'), ...files.map((file) => ({ ...file, isNew: true }))],
    //   keepTouched: true,
    //   keepDirty: true,
    // });

    handleUploadModalClose();
  };

  const onCategoryAdd = ({ category }) => {
    handlePickCategoryModalClose();
    methods.setValue('category', category, { shouldDirty: true, shouldValidate: true });
  };

  const onAddToSet = ({ sets }) => {
    methods.setValue('sets', sets, { shouldDirty: true });

    handleAddToSetModalClose();
  };

  const handleSearchByColorModalOpen = () => {
    toggleSearchByColorModal(true);
  };

  const handleSearchByColorModalClose = () => {
    toggleSearchByColorModal(false);
  };

  const onSearchByColor = ({ colors }) => {
    methods.setValue('colors', colors, { shouldDirty: true, shouldTouch: true });

    toggleSearchByColorModal(false);
  };

  const colorsValidation = yup.object().shape({
    colors: yup.array().of(yup.string().required()).min(1),
  });

  const modalFieldHandlers = {
    handleUploadModalOpen,
    handlePickCategoryModalOpen,
    handleAddToSetModalOpen,
    handleSearchByColorModalOpen,
    handleAddItemsToWishlistModalOpen,
    handleDeleteItemsModalOpen,
  };

  const wishlistSubmit = () => {
    dispatch(showSuccessNotification({
      message: 'You successfully added {X} items to the wishlist.',
    }));
  };

  const deleteItemsSubmit = () => {
    dispatch(deleteItems({ ids: [data.id] }));
  };

  const onImageUrlUpload = (urlData) => {
    dispatch(createImageByUrl(urlData));
  };

  const onImageUrlReset = () => {
    dispatch(resetImageByUrl());
  };

  return (
    <Container
      isOpen={isOpened}
      overlayClassName="Overlay"
      shouldFocusAfterRender={false}
      closeTimeoutMS={TransitionDuration}
      width="718px"
    >
      <Header
        title="Add item manually"
        onClose={() => {
          dispatch(resetNextItemData());

          onClose(true);
        }}
      />
      <FormProvider {...methods}>
        <StyledForm>
          <SliderContainer>
            <SliderField
              name="images"
              required
              key="itemImagesSlider"
              isEditable={!isUploadOpened}
            />
          </SliderContainer>
          <Content>
            <ItemFields
              isEditable
              isCreate
              modalFieldHandlers={modalFieldHandlers}
              labelMaps={{
                setMap,
                categoryMap,
                manufacturerMap: labelMaps?.manufacturers,
                storageMap: labelMaps?.storages,
              }}
              disabledFields={disabledFields}
              isAdditionalDataLoadDenied={false}
            />
          </Content>
        </StyledForm>
      </FormProvider>
      <FooterContainer footer contrasting={false} isLongerFooter={false} isThirdActionEnabled={isAddMoreEnabled}>
        <CancelButton
          cancelWidth="94px"
          onClick={() => {
            dispatch(resetNextItemData());

            onClose(true);

            onCancel?.();
          }}
        >
          {cancelText}
        </CancelButton>
        <ConfirmButton
          // submitWidth="118px"
          aria-label={saveText}
          // submitWidth="120px"
          disabled={!isValid}
          onClick={methods.handleSubmit((values) => handleSubmit(values))}
        >
          <span>
            {saveText}
          </span>
        </ConfirmButton>
        {
          isAddMoreEnabled ? (
            <ConfirmButton
              onClick={methods.handleSubmit((values) => handleSubmit(values, true))}
              disabled={!isValid}
            >
              <span>
                {saveAndCreateAnotherButtonText}
              </span>
            </ConfirmButton>
          ) : null
        }
      </FooterContainer>
      <UploadModal
        text=""
        contrastingFooter
        name="file"
        title="Add image"
        submitText="Add image"
        isOpened={isUploadOpened}
        onClose={handleUploadModalClose}
        onReset={onFileRest}
        onUpload={onFileUpload}
        onImageUrlUpload={onImageUrlUpload}
        onResetImageUrl={onImageUrlReset}
        isLoading={isImageLoading || isImageByUrlLoading}
        newImageData={newImageData}
        newImageByUrlData={newImageByUrlData}
        shouldUnregister={false}
        onSubmit={onFileSubmit}
        defaultValues={{
          files: [],
          images: [],
          urls: [{ value: '' }],
        }}
        validation={fileValidation}
        overlayClassName="Overlay"
      />
      <PickRadioModal
        text=""
        name="category"
        title="Pick a category"
        placeholder="Search for a category"
        submitText="Save changes"
        cancelText="Cancel"
        isOpened={isPickCategoryModalOpened}
        onClose={handlePickCategoryModalClose}
        onSubmit={onCategoryAdd}
        validation={categoryValidation}
        overlayClassName="Overlay"
        cancelWidth="94px"
        options={categories}
        defaultValue={methods.getValues('category')}
        onSearch={onCategoryQueryChange}
      />
      <PickAndCreateSet
        isSetList
        isMultipleAllowed
        text=""
        name="sets"
        title="Add item to sets"
        placeholder="Search for sets"
        submitText="Save changes"
        cancelText="Cancel"
        isOpened={isAddToSetModalOpened}
        isLoading={isSetListLoading}
        onClose={handleAddToSetModalClose}
        onSubmit={onAddToSet}
        options={setOptions}
        defaultValue={methods.getValues('sets')}
        validation={setValidation}
        overlayClassName="Overlay"
        cancelWidth="94px"
        onSearch={onSetQueryChange}
      />
      <SearchByColorModal
        text=""
        isCancelDisplayed={false}
        contrastingFooter
        name="colors"
        title="Edit colors"
        submitText="Submit selected colors"
        isOpened={isSearchByColorModalOpened}
        onClose={handleSearchByColorModalClose}
        onSubmit={onSearchByColor}
        defaultTab={TABS.PICTURE}
        validation={colorsValidation}
        defaultValue={methods.getValues('colors')}
        images={methods.getValues('images')}
        overlayClassName="Overlay"
      />
      <AddWishlistModal
        text={`You are about to add "${data?.name}" to wishlist, are you sure?`}
        itemText={data?.name}
        title="Add item to wishlist"
        submitText="Yes"
        cancelText="Cancel"
        isOpened={isAddItemsToWishlistModalOpened}
        onClose={handleAddItemsToWishlistModalClose}
        onSubmit={wishlistSubmit}
        overlayClassName="Overlay"
      />
      <DeleteItemsModal
        text="You are about to delete selected items, are you sure?"
        itemText={data?.name}
        title="Delete item?"
        submitText="Yes"
        cancelText="Cancel"
        isOpened={isDeleteItemsModalOpened}
        onClose={handleDeleteItemsModalClose}
        onSubmit={deleteItemsSubmit}
        overlayClassName="Overlay"
      />
    </Container>
  );
};

export default ManuallyModal;
